import { initFilters } from './ui/filters.js';
import { initTable } from './ui/table.js';
import { initModal } from './ui/modal.js';
import { initPartyBuilder } from './ui/party.js';
import { initStats } from './ui/stats.js';
import { initIdentity, maybeShowIdentityModal } from './ui/identity.js';
import { initLocations } from './ui/locations.js';
import { initTheme } from './ui/theme.js';
import { initTabs } from './ui/tabs.js';
import { initMemoriam } from './ui/memoriam.js';
import { initActivity } from './ui/activity.js';
import {
  setCharacters,
  setPlayers,
  setLocations,
  setSessions,
  setNeverForgetEvents,
  setLevelUpEvents,
  setLoading,
  addCharacterLocal,
  addPlayerLocal,
  subscribe,
  getState
} from './state/store.js';
import {
  fetchCharacters,
  fetchPlayers,
  fetchLocations,
  fetchSessions,
  fetchNeverForgetEvents,
  fetchLevelUpEvents,
  createCharacter,
  createPlayer,
  updateCharacter,
  uploadPortrait
} from './services/supabase.js';
import { initCalendar } from './ui/calendar.js';
import { RACE_OPTIONS } from './utils/character-options.js';
import { normalizeTimeJailValue } from './utils/time-jail.js';
import { getLocationOptions } from './utils/locations.js';
import { applyPlayersToCharacters, getPlayerByName, getPlayerOptions } from './utils/players.js';
import { initFormPillGroups, renderSingleSelectPillGroup } from './utils/pill-select.js';

const newPlayerBtn = document.getElementById('newPlayerBtn');
const addCharacterBtn = document.getElementById('addCharacterBtn');
const playerFormModal = document.getElementById('playerFormModal');
const playerForm = document.getElementById('playerForm');
const playerFormCancel = document.getElementById('playerFormCancel');
const characterFormModal = document.getElementById('characterFormModal');
const characterForm = document.getElementById('characterForm');
const characterFormCancel = document.getElementById('characterFormCancel');
const characterFormPlate = document.getElementById('characterFormPlate');
const characterFormPortraitInput = document.getElementById('characterFormPortraitInput');
let newCharacterPortraitUrl = '';
const filtersPanel = document.getElementById('filtersPanel');
const toggleFiltersBtn = document.getElementById('toggleFiltersBtn');
const closeFiltersBtn = document.getElementById('closeFiltersBtn');
const addMenuFab = document.getElementById('addMenuFab');
const addMenuOverlay = document.getElementById('addMenuOverlay');

const syncExpiredTimeJail = async (characters) => {
  const expired = characters.filter((c) => c.time_jail && !normalizeTimeJailValue(c.time_jail));
  if (!expired.length) return;
  await Promise.all(
    expired.map((character) =>
      updateCharacter(character.id, { time_jail: '' }).catch((error) => {
        console.error(`Failed to clear Time Jail for ${character.name || character.id}:`, error);
      })
    )
  );
};

const loadRoster = async () => {
  try {
    setLoading(true);
    const [characters, players, locations, sessions, neverForgetEvents, levelUpEvents] = await Promise.all([
      fetchCharacters(),
      fetchPlayers().catch((error) => {
        console.warn(`Players table unavailable: ${error.message}`);
        return [];
      }),
      fetchLocations().catch((error) => {
        console.warn(`Locations table unavailable: ${error.message}`);
        return [];
      }),
      fetchSessions().catch((error) => {
        console.warn(`Sessions table unavailable: ${error.message}`);
        return [];
      }),
      fetchNeverForgetEvents().catch((error) => {
        console.warn(`Never Forget events table unavailable: ${error.message}`);
        return [];
      }),
      fetchLevelUpEvents().catch((error) => {
        console.warn(`Level Up events table unavailable: ${error.message}`);
        return [];
      })
    ]);
    await syncExpiredTimeJail(characters);
    setPlayers(players);
    setCharacters(applyPlayersToCharacters(characters, players));
    setLocations(locations);
    setSessions(sessions);
    setNeverForgetEvents(neverForgetEvents);
    setLevelUpEvents(levelUpEvents);
  } catch (error) {
    console.error(error);
    alert(`Failed to load roster: ${error.message}`);
  } finally {
    setLoading(false);
  }
};

const openCharacterForm = () => {
  characterForm?.reset();
  newCharacterPortraitUrl = '';
  if (characterFormPlate) {
    characterFormPlate.style.backgroundImage = '';
    characterFormPlate.classList.remove('has-image');
    const hint = characterFormPlate.querySelector('.plate-hint');
    if (hint) hint.textContent = 'Portrait';
  }
  renderCharacterFormPlayerPillGroups(getState());
  renderCharacterFormRacePillGroups();
  renderCharacterFormLocationPillGroups(getState());
  initFormPillGroups(characterFormModal);
  characterFormModal?.showModal();
};

const handlePortraitInputChange = async (event) => {
  const file = event.target.files?.[0];
  if (!file || !characterFormPlate) return;
  const hint = characterFormPlate.querySelector('.plate-hint');
  if (hint) hint.textContent = 'Uploading…';
  try {
    newCharacterPortraitUrl = await uploadPortrait(file);
    characterFormPlate.style.backgroundImage = `url("${newCharacterPortraitUrl}")`;
    characterFormPlate.classList.add('has-image');
  } catch (error) {
    alert(`Portrait upload failed: ${error.message}`);
    if (hint) hint.textContent = 'Portrait';
  } finally {
    event.target.value = '';
  }
};

const closeCharacterForm = () => characterFormModal?.close();
const openPlayerForm = () => {
  playerForm?.reset();
  playerFormModal?.showModal();
};
const closePlayerForm = () => playerFormModal?.close();

const renderCharacterFormPlayerPillGroups = (state) => {
  characterForm
    ?.querySelectorAll('[data-form-player-select]')
    .forEach((group) => {
      const current = group.querySelector('input[type="hidden"]')?.value || '';
      renderSingleSelectPillGroup(group, getPlayerOptions(state, current));
    });
};

const renderCharacterFormLocationPillGroups = (state) => {
  characterForm
    ?.querySelectorAll('[data-form-location-select]')
    .forEach((group) => {
      const current = group.querySelector('input[type="hidden"]')?.value || '';
      renderSingleSelectPillGroup(group, getLocationOptions(state, current));
    });
};

const renderCharacterFormRacePillGroups = () => {
  characterForm
    ?.querySelectorAll('[data-form-race-select]')
    .forEach((group) => {
      renderSingleSelectPillGroup(group, RACE_OPTIONS);
    });
};

const handleCharacterSubmit = async (event) => {
  event.preventDefault();
  const formData = Object.fromEntries(new FormData(characterForm).entries());
  if (!formData.character_name) {
    alert('Character name is required.');
    return;
  }
  if (
    String(formData.status || '').trim().toLowerCase() === 'dead' &&
    (!formData.deathday || !formData.cause_of_death)
  ) {
    alert('Deathday and Cause of Death are required when Status is Dead.');
    return;
  }
  const selectedPlayer = getPlayerByName(getState().players, formData.player);
  const playerName = String(formData.player || '').trim();
  if (!playerName) {
    formData.player = '';
    formData.discord_id = '';
  } else if (selectedPlayer) {
    formData.player = selectedPlayer.name;
    formData.discord_id = selectedPlayer.discord_id || '';
  } else {
    formData.player = playerName;
    formData.discord_id = '';
  }
  formData.portrait_url = newCharacterPortraitUrl;
  try {
    const created = await createCharacter(formData);
    addCharacterLocal(applyPlayersToCharacters([created], getState().players)[0]);
    closeCharacterForm();
    alert('Character added.');
  } catch (error) {
    alert(`Failed to add character: ${error.message}`);
  }
};

const handlePlayerSubmit = async (event) => {
  event.preventDefault();
  const formData = Object.fromEntries(new FormData(playerForm).entries());
  const playerName = String(formData.name || '').trim();
  if (!playerName) {
    alert('Player name is required.');
    return;
  }
  try {
    const created = await createPlayer({
      name: playerName,
      discord_id: formData.discord_id
    });
    addPlayerLocal(created);
    closePlayerForm();
    alert('Player added.');
  } catch (error) {
    alert(`Failed to add player: ${error.message}`);
  }
};

const attachEvents = () => {
  newPlayerBtn?.addEventListener('click', openPlayerForm);
  addCharacterBtn.addEventListener('click', openCharacterForm);
  playerForm?.addEventListener('submit', handlePlayerSubmit);
  playerFormCancel?.addEventListener('click', closePlayerForm);
  characterForm.addEventListener('submit', handleCharacterSubmit);
  characterFormCancel.addEventListener('click', closeCharacterForm);
  characterFormPortraitInput?.addEventListener('change', handlePortraitInputChange);
  initFormPillGroups();
  if (toggleFiltersBtn && filtersPanel) {
    toggleFiltersBtn.addEventListener('click', () => {
      filtersPanel.classList.toggle('open');
    });
    if (closeFiltersBtn) {
      closeFiltersBtn.addEventListener('click', () => {
        filtersPanel.classList.remove('open');
      });
    }
    filtersPanel.addEventListener('click', (event) => {
      const closeTrigger = event.target.closest('[data-close-filters]');
      if (!closeTrigger) return;
      event.preventDefault();
      filtersPanel.classList.remove('open');
    });
    // close drawer when tapping outside (by clicking layout)
    document.addEventListener('click', (event) => {
      if (!filtersPanel.classList.contains('open')) return;
      if (
        filtersPanel.contains(event.target) ||
        (toggleFiltersBtn && toggleFiltersBtn.contains(event.target))
      ) {
        return;
      }
      filtersPanel.classList.remove('open');
    });
    window.addEventListener('resize', () => {
      if (window.innerWidth > 900) filtersPanel.classList.remove('open');
    });
  }
  if (addMenuFab && addMenuOverlay) {
    addMenuFab.addEventListener('click', () => {
      addMenuOverlay.hidden = false;
    });
    addMenuOverlay.addEventListener('click', (event) => {
      if (event.target === addMenuOverlay || event.target.closest('.add-menu-row')) {
        addMenuOverlay.hidden = true;
      }
    });
  }
};

const init = () => {
  initTheme();
  initIdentity();
  initTabs();
  initFilters();
  initTable();
  initModal();
  initPartyBuilder();
  initStats();
  initCalendar();
  initMemoriam();
  initActivity();
  initLocations();
  attachEvents();
  subscribe((state) => {
    renderCharacterFormPlayerPillGroups(state);
    renderCharacterFormLocationPillGroups(state);
    const { loading } = state;
    document.body.classList.toggle('loading', !!loading);
  });
  renderCharacterFormPlayerPillGroups(getState());
  renderCharacterFormRacePillGroups();
  renderCharacterFormLocationPillGroups(getState());
  loadRoster().then(maybeShowIdentityModal);
};

document.addEventListener('DOMContentLoaded', init);
